import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tv, Clock, Sparkles } from "lucide-react";
import api from "../lib/api";

/**
 * KidsTvGuide — 24-hour kid-safe schedule grid.
 *
 * Slots come from /api/kids-public/tv-guide. "Now" slot is highlighted;
 * future slots are dim. Tapping a slot routes to /kids/watch/:id.
 */
export default function KidsTvGuide() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/kids-public/tv-guide")
      .then((r) => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="kids-tv-guide-page" className="pb-20 px-3 sm:px-6 md:px-10 max-w-6xl mx-auto text-white">
      <div className="relative overflow-hidden rounded-3xl p-5 sm:p-8 mt-3 bg-gradient-to-br from-sky-400 via-indigo-500 to-purple-700 shadow-2xl ring-4 ring-white/25">
        <div className="absolute -top-8 -right-8 opacity-25 pointer-events-none text-[10rem]">📺</div>
        <div className="relative flex items-center gap-3 flex-wrap">
          <img src="/kids-learn-play-logo.png" alt="" className="w-20 sm:w-28 h-auto drop-shadow-2xl" />
          <div>
            <div className="inline-flex items-center gap-1 bg-white/20 text-white text-[10px] uppercase tracking-[0.25em] font-black px-2 py-0.5 rounded-full backdrop-blur"><Sparkles className="w-3 h-3" /> Kids TV Guide</div>
            <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter drop-shadow mt-1">What's on</h1>
            <p className="text-white/90 text-sm">A whole day of fun, hour by hour.</p>
          </div>
        </div>
      </div>

      {loading && <div className="mt-8 text-white/60 text-center">Loading guide…</div>}

      <div data-testid="tv-guide-grid" className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {slots.map((s, i) => {
          const isNow = i === 0;
          return (
            <Link
              key={s.start + s.video_id}
              to={`/kids/watch/${s.video_id}`}
              data-testid={`tv-guide-slot-${i}`}
              className={`group flex items-stretch gap-3 rounded-2xl overflow-hidden bg-black/30 border border-white/10 hover:border-pink-400 transition ${isNow ? "ring-2 ring-yellow-300 shadow-lg shadow-yellow-400/20" : ""}`}
            >
              <div className="w-24 sm:w-28 aspect-video bg-black shrink-0 rounded-l-2xl overflow-hidden">
                {s.thumbnail_url
                  ? <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-gradient-to-br from-fuchsia-500 to-sky-500 flex items-center justify-center text-2xl">🎬</div>}
              </div>
              <div className="flex-1 min-w-0 py-2 pr-3">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-black">
                  <Clock className="w-3 h-3 text-white/60" />
                  <span className="text-white/70">{s.hour_label}</span>
                  {isNow && <span className="bg-yellow-300 text-black px-1.5 py-0.5 rounded-full">ON NOW</span>}
                </div>
                <div className="mt-0.5 font-bold text-sm truncate">{s.title}</div>
                {s.creator_name && <div className="text-white/50 text-[10px] truncate">@{s.handle || s.creator_name}</div>}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
